import React from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, MessageCircle, ArrowUpRight } from "lucide-react";
import { LINKS } from "@/lib/links";

const plans = [
  {
    id: "social-presence",
    name: "Social Presence",
    tagline:
      "Per attività che vogliono comunicare con costanza, aumentare visibilità e costruire fiducia sui social.",
    price: "da €500",
    suffix: "/mese",
    features: [
      "Produzione contenuti a partire da 8 video mensili",
      "Piano editoriale operativo",
      "Format personalizzati per il tuo settore",
      "Pubblicazione e organizzazione contenuti",
      "Revisione mensile dei risultati",
    ],
    highlight: false,
  },
  {
    id: "growth-system",
    name: "Growth System",
    tagline:
      "Per attività che vogliono unire social, sito e strategia in un unico sistema di crescita.",
    price: "da €900",
    suffix: "/mese + setup",
    features: [
      "Gestione social mensile",
      "Contenuti video e format strategici",
      "Sito o landing page orientata alla conversione",
      "Percorso verso WhatsApp, prenotazioni o richieste",
      "Report e ottimizzazione mensile",
      "Possibilità di integrare campagne Meta o Google Ads",
    ],
    highlight: true,
    badge: "Più scelto",
  },
  {
    id: "web-presence",
    name: "Web Presence",
    tagline:
      "Per attività che vogliono un sito moderno, chiaro e capace di trasformare visite in richieste.",
    price: "da €900 setup",
    suffix: " + da €120/mese",
    features: [
      "Sito vetrina, gestionale o struttura Shopify",
      "Copywriting delle sezioni principali",
      "Design responsive",
      "Ottimizzazione mobile",
      "Collegamento WhatsApp, social e strumenti di contatto",
      "Gestione aggiornamenti e supporto mensile",
    ],
    highlight: false,
  },
];

export default function Pricing() {
  return (
    <section
      id="pacchetti"
      data-testid="pricing-section"
      className="relative isolate py-20 md:py-32 overflow-hidden text-rim-text bg-rim-surfaceAlt"
    >
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,rgba(168,85,247,0.18),transparent_60%)]" />
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_bottom_left,rgba(124,58,237,0.18),transparent_60%)]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 md:mb-16">
          <h2
            data-testid="pricing-title"
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter uppercase text-white leading-[0.92]"
          >
            Percorsi
          </h2>
          <p className="mt-5 font-heading text-xl md:text-2xl lg:text-3xl font-bold tracking-tight text-rim-glow leading-snug max-w-2xl">
            Scegli il tuo punto di partenza.
          </p>
          <p className="mt-4 text-[15px] md:text-lg text-rim-muted leading-relaxed max-w-2xl">
            Quanto ti costa oggi non avere una comunicazione chiara? Ogni
            pacchetto è pensato per aiutarti a trasformare contenuti, immagine
            e strategia in una presenza digitale più professionale e capace di
            generare fiducia.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-4 md:gap-6 items-stretch">
          {plans.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              data-testid={`pricing-card-${p.id}`}
              className={`relative rounded-3xl p-6 md:p-8 flex flex-col ${
                p.highlight
                  ? "bg-gradient-to-b from-rim/30 to-rim-surface border-2 border-rim shadow-[0_30px_80px_-20px_rgba(168,85,247,0.6)] lg:-translate-y-3"
                  : "bg-rim-surface border border-rim/25"
              }`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1.5 bg-rim text-white text-[11px] font-semibold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full shadow-lg">
                    <Sparkles className="h-3.5 w-3.5" />
                    {p.badge}
                  </span>
                </div>
              )}

              <h3 className="font-heading text-xl md:text-2xl font-bold text-white">
                {p.name}
              </h3>

              <p className="mt-2 text-[13px] leading-relaxed text-rim-muted">
                {p.tagline}
              </p>

              <div className="mt-5 flex items-baseline flex-wrap gap-1.5">
                <span className="font-heading text-3xl md:text-4xl font-black tracking-tight text-white">
                  {p.price}
                </span>
                <span className="text-sm text-rim-muted">{p.suffix}</span>
              </div>

              <div className="my-5 h-px bg-rim/20" />

              <ul className="space-y-2.5 mb-6 flex-1">
                {p.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-3 text-[14px] leading-snug"
                  >
                    <span className="mt-0.5 h-5 w-5 rounded-full grid place-items-center shrink-0 bg-rim text-white">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    <span className="text-white/85">{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href={LINKS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`pricing-cta-${p.id}`}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-bold transition-all duration-300 bg-rim hover:bg-rim-hover text-white hover:shadow-[0_10px_30px_-8px_rgba(168,85,247,0.7)]"
              >
                <MessageCircle className="h-4 w-4" />
                Scrivici ora
              </a>
            </motion.div>
          ))}
        </div>

        <motion.a
          href={LINKS.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="pricing-custom-quote"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mt-10 md:mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 p-6 md:p-8 rounded-3xl bg-rim-surface border border-rim/30 hover:border-rim/60 hover:shadow-[0_30px_80px_-25px_rgba(168,85,247,0.5)] transition-all duration-500"
        >
          <div>
            <p className="font-heading text-xl md:text-2xl font-bold text-white leading-tight">
              Vuoi un preventivo personalizzato?
            </p>
            <p className="mt-1.5 text-[14px] md:text-base text-rim-muted">
              Raccontaci il tuo progetto e costruiamo una proposta su misura.
            </p>
          </div>
          <span className="inline-flex items-center justify-center gap-2 bg-rim hover:bg-rim-hover text-white text-sm font-semibold px-6 py-3.5 rounded-full shrink-0 transition-colors shadow-[0_10px_25px_-8px_rgba(168,85,247,0.7)]">
            <MessageCircle className="h-4 w-4" />
            Contattaci
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </motion.a>
      </div>
    </section>
  );
}
